import React, { memo, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'

import { Badge, Loading } from 'components'
import styles from '../styles'
import { EEntity, TSearchPeopleProps } from 'models'
import { StoreValue } from 'store'
import { SearchEmptyResult } from '.'
import { useProfileCollectionQuery } from 'graphql/__generated__/graphql'

const useStyles = makeStyles(styles)

const People: React.FunctionComponent<TSearchPeopleProps> = ({
	history,
	storeSearchDebounceQuery,
}) => {
	const classes = useStyles()
	const { i18n } = useTranslation()

	const { data, fetchMore } = useProfileCollectionQuery({
		skip: !storeSearchDebounceQuery,
		variables: {
			first: 10,
			filter: {
				or: [{ full_name: { ilike: `%${storeSearchDebounceQuery}%` } }],
			},
		},
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: false,
	})

	const hasNextPage = data?.profileCollection?.pageInfo
		? data?.profileCollection?.pageInfo?.hasNextPage
		: false

	const people = data?.profileCollection?.edges
		? data.profileCollection?.edges?.map((edge: any) => edge.node)
		: []

	const goToProfile = useCallback(
		(user: any) => () => {
			history.push(`/${i18n.language}/people/${user.user.uid}`)
		},
		[history, i18n.language],
	)

	return (
		<>
			{!data?.profileCollection &&
			data?.profileCollection?.edges.length === 0 ? (
				<SearchEmptyResult query={storeSearchDebounceQuery} />
			) : (
				<div
					className={classNames(
						classes.tabsContentContainer,
						classes.fixFlexList,
					)}
				>
					<InfiniteScroll
						pageStart={0}
						loadMore={() => {
							return fetchMore({
								variables: {
									query: storeSearchDebounceQuery,
									after: data?.profileCollection?.pageInfo
										.endCursor,
								},
								updateQuery: (
									previousQueryResult,
									{ fetchMoreResult },
								) => {
									if (
										!fetchMoreResult ||
										!fetchMoreResult.profileCollection
									) {
										return previousQueryResult
									}

									const { edges: newEdges, pageInfo } =
										fetchMoreResult.profileCollection

									return newEdges.length > 0
										? {
												profileCollection: {
													__typename:
														previousQueryResult
															?.profileCollection
															?.__typename,
													edges: [
														...(previousQueryResult
															?.profileCollection
															?.edges || []),
														...newEdges,
													],
													pageInfo,
												},
										  }
										: previousQueryResult
								},
							})
						}}
						hasMore={hasNextPage}
						loader={
							<div
								style={{ flexGrow: 1, display: 'flex' }}
								key={'loading'}
							>
								<Loading
									size={48}
									style={{ margin: '50px auto' }}
								/>
							</div>
						}
						className={classes.searchContentCards}
					>
						{people?.map((item: any, index: number) => {
							return (
								<Badge
									{...item}
									fullView
									type={EEntity.person}
									user={item}
									title={`${item.firstName} ${item.lastName}`}
									key={`people_${index}`}
									extraClasses={classes.companyBadge}
									currentPositionClasses={
										classes.peopleBadgeCurrentPosition
									}
									onClick={goToProfile(item)}
								/>
							)
						})}
					</InfiniteScroll>
				</div>
			)}
		</>
	)
}

export default memo((props: TSearchPeopleProps) => (
	<StoreValue
		keys={[
			'search.debounceQuery|storeSearchDebounceQuery',
			'search.loading|storeSearchLoading',
		]}
	>
		<People {...props} />
	</StoreValue>
))
